import { useState } from "react";
import { Card, CardContent, Chip, Typography, Box, Button } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/es';

// Configurar plugins de dayjs
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('es');

export const NewsContent = ({ news }) => {
    const [likeCount, setLikeCount] = useState(0);
    const [hasLiked, setHasLiked] = useState(false);
    // Función para formatear fecha con zona horaria en español
    const formatDate = (dateString) => {
        return dayjs(dateString)
            .tz('America/Santiago')
            .format('D [de] MMMM [de] YYYY');
    };

    // Función para calcular el tiempo de lectura aproximado
    const calculateReadTime = (text) => {
        if (!text) return 0;
        const cleanText = text.replace(/<[^>]*>/g, '');
        const wordCount = cleanText.split(/\s+/).length;
        const readingTime = Math.max(1, Math.ceil(wordCount / 200)); // Mínimo 1 minuto
        return readingTime;
    };

    const handleLike = () => {
        if (hasLiked) {
            setLikeCount(likeCount - 1);
        } else {
            setLikeCount(likeCount + 1);
        }
        setHasLiked(!hasLiked);
    };

    return (
        <Box sx={{ 
            bgcolor: 'white', 
            borderRadius: { xs: '12px', md: '16px' }, 
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
            {/* Cabecera con título y metadatos */}
            <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                {/* Categoría */}
                {news.category && (
                    <Chip 
                        label={news.category} 
                        color="primary" 
                        size="small" 
                        sx={{ 
                            mb: 2, 
                            fontWeight: 'bold',
                            backgroundColor: '#1976d2',
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            height: { xs: '24px', sm: '26px' }
                        }} 
                    />
                )}
                
                {/* Título */}
                <Typography 
                    variant="h3" 
                    component="h1" 
                    sx={{ 
                        fontWeight: '700',
                        color: '#222',
                        mb: 3,
                        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.25rem' },
                        lineHeight: 1.3
                    }}
                >
                    {news.title}
                </Typography>
                
                {/* Metadatos - Optimizados para móvil */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    mb: 3,
                    gap: { xs: 2, sm: 0 }
                }}>
                    {/* Info del autor y fecha */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        flexWrap: 'wrap',
                        width: { xs: '100%', sm: 'auto' }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <div style={{ 
                                display: 'flex', 
                                height: '36px', 
                                width: '36px', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                borderRadius: '50%', 
                                backgroundColor: '#f0f0f0'
                            }}>
                                <PersonIcon style={{ height: '18px', width: '18px', color: '#666' }} />
                            </div>
                            <Typography 
                                variant="subtitle2" 
                                sx={{ fontWeight: 500, color: '#333' }}
                            >
                                {news.company?.trade_name || 'Autor desconocido'}
                            </Typography>
                        </Box>
                        
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: { xs: 2, sm: 2 },
                            ml: { xs: 0, sm: 2 },
                            flexWrap: 'wrap'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarTodayIcon fontSize="small" sx={{ color: '#666', fontSize: '0.9rem' }} />
                                <Typography variant="caption" sx={{ color: '#555', fontSize: '0.8rem' }}>
                                    {formatDate(news.creationDate)}
                                </Typography>
                            </Box>
                        
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTimeIcon fontSize="small" sx={{ color: '#666', fontSize: '0.9rem' }} />
                                <Typography variant="caption" sx={{ color: '#555', fontSize: '0.8rem' }}>
                                    {calculateReadTime(news.description)} min de lectura
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    
                    {/* Aquí iría el botón de "Like" si se descomenta */}
                </Box>
            </Box>

            {/* Imagen principal */}
            <Box 
                sx={{ 
                    width: '100%', 
                    position: 'relative', 
                    mb: 3, 
                    border: '4px solid #e0e0e0',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
            >
                <img
                    src={news.image}
                    alt={news.title}
                    style={{ 
                        width: '100%', 
                        display: 'block',
                        maxHeight: '600px',
                        objectFit: 'cover'
                    }}
                    onError={(e) => {
                        e.target.onerror = null; 
                        e.target.src = 'https://via.placeholder.com/1200x600?text=Imagen+no+disponible';
                    }}
                />
            </Box>

            {/* Contenido principal */}
            <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                {/* Resumen/Lead */}
                {news.lead && (
                    <Card sx={{ 
                        borderLeft: '4px solid #1976d2', 
                        mb: { xs: 3, sm: 4, md: 5 }, 
                        bgcolor: '#f7f9fc',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Typography 
                                component="div"
                                sx={{ 
                                    fontSize: { xs: '1rem', sm: '1.125rem' },
                                    fontWeight: 500,
                                    fontStyle: 'italic',
                                    lineHeight: 1.6
                                }}
                                dangerouslySetInnerHTML={{ __html: news.lead } }
                                style={{
                                    wordWrap: 'break-word', // Asegura que el texto largo se ajuste correctamente
                                    whiteSpace: 'normal', // Permite los saltos de línea cuando sea necesario
                                }}
                            />
                        </CardContent>
                    </Card>
                )}

                {/* Contenido completo */}
                <Box 
                    sx={{
                        typography: 'body1',
                        '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '8px',
                            margin: '1.5rem 0',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.1)'
                        },
                        '& iframe': {
                            maxWidth: '100%',
                            borderRadius: '8px',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.1)'
                        },
                        '& p': {
                            textAlign: { xs: 'left', sm: 'justify' },
                            lineHeight: 1.8,
                            fontSize: { xs: '0.95rem', sm: '1.05rem' },
                            color: '#333',
                            margin: '1rem 0'
                        },
                        '& h2, & h3': {
                            fontWeight: 600,
                            color: '#222',
                            marginTop: { xs: '1.5rem', sm: '2rem' },
                            marginBottom: '1rem',
                            fontSize: { xs: '1.3rem', sm: '1.5rem' }
                        },
                        '& blockquote': {
                            borderLeft: '4px solid #ddd',
                            paddingLeft: '1rem',
                            margin: '1.5rem 0',
                            fontStyle: 'italic',
                            color: '#555'
                        },
                        '& ul, & ol': {
                            paddingLeft: { xs: '1.2rem', sm: '1.5rem' },
                            margin: '1rem 0'
                        }
                    }}
                >
                    <div dangerouslySetInnerHTML={{ __html: news.description }} style={{
              wordWrap: 'break-word', // Asegura que el texto largo se ajuste correctamente
              whiteSpace: 'normal', // Permite los saltos de línea cuando sea necesario
            }} />
                </Box>
                
                {/* Sección de etiquetas */}
                {news.tags && news.tags.length > 0 && (
                    <Box sx={{ mt: { xs: 4, sm: 6 }, pt: { xs: 3, sm: 4 }, borderTop: '1px solid #eaeaea' }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                            Temas relacionados:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {news.tags.map((tag, index) => (
                                <Chip 
                                    key={index}
                                    label={tag}
                                    clickable
                                    size={window.innerWidth < 600 ? "small" : "medium"}
                                    sx={{ 
                                        borderRadius: '16px',
                                        bgcolor: '#f0f0f0',
                                        '&:hover': { bgcolor: '#e0e0e0' },
                                        fontSize: { xs: '0.75rem', sm: '0.85rem' }
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
                
                {/* Banner de información */}
                <Card sx={{ 
                    mt: { xs: 5, sm: 8 }, 
                    bgcolor: '#f0f7ff', 
                    borderRadius: '12px',
                    boxShadow: 'none',
                    border: '1px solid #d0e1f9'
                }}>
                    <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ 
                                mb: 1, 
                                color: '#1976d2', 
                                fontWeight: 600,
                                fontSize: { xs: '1.1rem', sm: '1.25rem' }
                            }}>
                                ¿Te gustó este artículo?
                            </Typography>
                            <Typography sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                Mantente informado con las últimas noticias en nuestra plataforma.
                            </Typography>
                            <Button 
                                variant="contained" 
                                color="primary"
                                sx={{ 
                                    borderRadius: '28px', 
                                    px: { xs: 3, sm: 4 }, 
                                    py: { xs: 0.8, sm: 1 },
                                    fontSize: { xs: '0.85rem', sm: '0.95rem' }
                                }}
                                onClick={() => window.location.href = '/news'}
                            >
                                Ver más noticias
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};